import { autoinject } from 'aurelia-framework';
import { ValidationController, Validator } from 'aurelia-validation';
import { I18N } from 'aurelia-i18n';
import {
  AquacomComponentCategory,
  MooringArticleCategory,
  WeightUnitType,
  categoryDimensionTypes,
  getArticleWeightType,
  weightUniOptions,
  MooringArticleCategoryType,
} from 'models/mooring';
import { MooringArticleCategoryService, MooringArticleCategoryTypeService, ToastService } from 'services';
import { DialogController } from 'aurelia-dialog';
import { AsyncErrorHandler } from 'lib/ui';
import { StandardValidationBuilder } from 'lib/validation';

const createOptions = (t: I18N) =>
  JSON.parse(JSON.stringify(categoryDimensionTypes)).map((x: (typeof categoryDimensionTypes)[number]) => {
    x.label = t.tr(x.label);
    return x;
  });

@autoinject
export class MooringArticleCategoryCreateEdit {
  protected componentTypes: MooringArticleCategoryType[] = [];
  protected aquacomCategories: AquacomComponentCategory[] = [];

  protected ready = false;

  protected form: MooringArticleCategory = {
    Id: undefined,
    Name: undefined,
    NameEn: undefined,
    NameEs: undefined,
    PrimaryDimensionType: undefined,
    SecondaryDimensionType: undefined,
    WeightUnitType: WeightUnitType.Kg_m,
    MooringArticleCategoryTypeId: undefined,
  };

  protected primaryDimensionTypes = '';
  protected secondaryDimensionTypes = '';

  constructor(
    protected validator: Validator,
    private validationController: ValidationController,
    private mooringArticleCategoryService: MooringArticleCategoryService,
    private mooringArticleCategoryTypeService: MooringArticleCategoryTypeService,
    private t: I18N,
    private toast: ToastService,
    private dialog: DialogController,
    private validationBuilder: StandardValidationBuilder
  ) {
    this.primaryDimensionTypes = createOptions(this.t);
    this.secondaryDimensionTypes = createOptions(this.t);
  }

  protected applyValidationRules() {
    this.validationBuilder
      .with(this.form)
      .required('Name')
      .required('NameEn')
      .required('PrimaryDimensionType')
      .required('MooringArticleCategoryTypeId')
      .done();
  }

  protected activate(params?: { id?: number }) {
    void this.setup(params?.id);
  }

  protected async setup(categoryId: number) {
    await this.getCategoryTypes();
    await this.getAquacomCategories();

    if (categoryId) {
      await this.getCategory(categoryId);
    }

    this.applyValidationRules();
    this.ready = true;
  }

  protected weightUnitOptions = weightUniOptions().map((x) => {
    x.label = this.t.tr(x.label);
    return x;
  });

  protected getWeightUnitLabel(type: WeightUnitType) {
    return getArticleWeightType(type);
  }

  @AsyncErrorHandler
  protected async getCategory(id: number) {
    const category = await this.mooringArticleCategoryService.get(id);
    this.form = {
      ...category,
    };
  }

  @AsyncErrorHandler
  protected async getAquacomCategories() {
    this.aquacomCategories = await this.mooringArticleCategoryService.getAquacomCategoryTypes();
  }

  @AsyncErrorHandler
  protected async getCategoryTypes() {
    this.componentTypes = await this.mooringArticleCategoryTypeService.getAll();
  }

  @AsyncErrorHandler
  protected async onSave(category: MooringArticleCategory) {
    if (category.Id) {
      delete category.MooringArticleCategoryType;
      await this.mooringArticleCategoryService.put(category, category.Id);
    } else {
      await this.mooringArticleCategoryService.post(category);
    }
    this.toast.showSuccess(this.t.tr('general.saved'));
    this.dialog.close(true);
  }

  @AsyncErrorHandler
  async save() {
    const { valid } = await this.validationController.validate();
    if (!valid) return;
    this.onSave?.(this.form);
  }

  @AsyncErrorHandler
  protected async onDelete() {
    if (!this.form.Id) return;
    await this.mooringArticleCategoryService.delete(this.form.Id);
    this.toast.showSuccess(this.t.tr('general.deleted'));
    this.cancel();
  }

  cancel() {
    this.dialog.close(false);
  }
}
